import type { FC } from 'react'

import styled from '@emotion/styled'

import Typography from '@segment/matcha/components/Typography'

import AccordionBase, * as Accordion from '@segment/matcha/recipes/Accordion'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import { useActiveState } from '@segment/utils/useActiveState'

const faqData = [
  {
    question: `What do customer engagement tools, like Twilio Engage, allow me to do?`,
    answer: `Customer engagement tools are a piece of software that monitors, manages, and enables interactions - from website engagement to support requests - in a single platform. They allow businesses to have a view of the entire customer experience in one place, and orchestrate personalized responses based on the behavior and profile of a user.`,
  },
  {
    question: `What features should I look for in customer engagement software?`,
    answer: `Identity resolution, cross channel tracking, robust integrations and the ability to build and activate a customer profile for personalized campaigns are some of the top requirements for an omnichannel customer engagement tool.`,
  },
]

const copy = {
  heading: `Frequently asked questions`,
}

const FaqSection: FC = () => {
  const items = new Array(faqData.length).fill(false)
  const { activeItems, toggle } = useActiveState(items, {
    allowMultipleActive: true,
  })

  return (
    <StyledSectionBase variant="noCopy">
      <Section.Contents>
        <Heading variant="h2">{copy.heading}</Heading>
        <Accordion.JsonLdFaq
          data={faqData.map(({ answer, question }) => {
            return {
              question,
              answer,
            }
          })}
        />
        {activeItems.map((active, index) => (
          <AccordionBase key={`item-${index}`}>
            <Accordion.Header onClick={() => toggle(index)}>
              <Accordion.TitleWrapper>
                <Accordion.Title tag="h3" opened={active}>
                  {faqData[index].question}
                </Accordion.Title>
              </Accordion.TitleWrapper>
              <Accordion.Icon opened={active} />
            </Accordion.Header>
            <Accordion.Body>
              {active && (
                <Accordion.ContentWrapper>
                  <Accordion.Text>{faqData[index].answer}</Accordion.Text>
                </Accordion.ContentWrapper>
              )}
            </Accordion.Body>
          </AccordionBase>
        ))}
      </Section.Contents>
    </StyledSectionBase>
  )
}

const Heading = styled(Typography)`
  padding-bottom: 28px;
  text-align: center;
  margin-top: 0;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    padding-bottom: 44px;
  }
`

const StyledSectionBase = styled(SectionBase)`
  max-width: 1004px;
  overflow-x: unset;

  ${Section.Wrapper} {
    padding-top: 0;
  }

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    padding-bottom: 40px;
  }
`

export default FaqSection
